// (B) Plain(플레인) Button 백엔드 — antd Button 대체. 순수 <button> + antd Button v5(5.29) DOM 이중클래스 미러링.
//   루트: .om-btn/.om-btn + legacy type(.om-btn-<type>)·color(.om-btn-color-<c>)·variant(.om-btn-variant-<v>)
//         + size·danger·block·loading·icon-only·icon-end 를 antd ButtonTypeMap 규칙대로 동시 부여
//         → 기존 소비처/전역 SCSS·ninegqlUI(antd Button 직접 렌더 공유표면)·JS(closest('.om-btn')) 전량 무변경 매칭.
//   아이콘: .om-btn-icon(+ loading 시 .om-btn-loading-icon) span 래핑. children 은 raw.
//   시각 base 는 styles/omwButton.css(:where 0-특이도, antd CSS-in-JS 가 주던 폴백만; 시각 대부분은 소비처 SCSS 담당).
import type { MouseEvent } from 'react'
import type { OmwButtonProps } from '../api/OmwButton'
import { cx } from './cx'
import '../styles/omwButton.css'

// OmwButton variant → antd [legacy type, color, variant](antd ButtonTypeMap, 5.29):
//   default→[default,default,outlined]·primary→[primary,primary,solid]·text→[text,default,text]·link→[link,link,link]
const VARIANT_MAP = {
  default: { type: 'default', color: 'default', variant: 'outlined' },
  primary: { type: 'primary', color: 'primary', variant: 'solid' },
  text: { type: 'text', color: 'default', variant: 'text' },
  link: { type: 'link', color: 'link', variant: 'link' },
} as const

// antd sizeClassNameMap: small→sm·large→lg·middle→''(클래스 없음).
const SIZE_CLASS = { sm: 'sm', md: '', lg: 'lg' } as const

// antd LoadingOutlined 대체(회전 3/4 원 SVG, @ant-design/icons 비의존).
function LoadingIcon() {
  return (
    <svg
      viewBox="0 0 1024 1024"
      width="1em"
      height="1em"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M512 1024c-69.1 0-136.2-13.5-199.3-40.2C251.7 958 197 921 150 874c-47-47-84-101.7-109.8-162.7C13.5 648.2 0 581.1 0 512c0-19.9 16.1-36 36-36s36 16.1 36 36c0 59.4 11.6 117 34.6 171.3 22.2 52.4 53.9 99.5 94.3 139.9 40.4 40.4 87.5 72.2 139.9 94.3C395 940.4 452.6 952 512 952c59.4 0 117-11.6 171.3-34.6 52.4-22.2 99.5-53.9 139.9-94.3 40.4-40.4 72.2-87.5 94.3-139.9C940.4 629 952 571.4 952 512c0-59.4-11.6-117-34.6-171.3a370.4 370.4 0 00-94.3-139.9 370.4 370.4 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.2C772.3 66 827 103 874 150c47 47 84 101.8 109.8 162.7 26.7 63.1 40.2 130.2 40.2 199.3s-13.5 136.2-40.2 199.3C958 772.3 921 827 874 874c-47 47-101.7 84-162.7 109.8-63.1 26.7-130.2 40.2-199.3 40.2z" />
    </svg>
  )
}

// om-btn-* 클래스 생성 헬퍼: btn('primary') → 'om-btn-primary'.
function btn(suffix: string): string {
  return `om-btn-${suffix}`
}

export function PlainButton({
  variant = 'default',
  size = 'md',
  disabled = false,
  loading = false,
  block = false,
  danger = false,
  primary = false,
  htmlType = 'button',
  icon,
  iconPosition = 'start',
  className,
  style,
  onClick,
  children,
  title,
  'aria-label': ariaLabel,
  'data-testid': dataTestid,
}: OmwButtonProps) {
  const map = VARIANT_MAP[variant]
  // danger 시 color 는 dangerous 로 치환(antd mergedColorText), legacy type·variant 는 유지.
  // primary prop 은 antd color="primary" 파리티 — variant 기본 color 를 primary 로 승격(danger 우선).
  const colorText = danger ? 'dangerous' : primary ? 'primary' : map.color
  const hasIconType = loading || icon != null
  const iconOnly = hasIconType && (children === undefined || children === null)
  const sizeCls = SIZE_CLASS[size]

  const rootClass = cx(
    'om-btn',
    btn(map.type),
    btn(`color-${colorText}`),
    btn(`variant-${map.variant}`),
    danger && btn('dangerous'),
    sizeCls !== '' && btn(sizeCls),
    loading && btn('loading'),
    block && btn('block'),
    iconOnly && btn('icon-only'),
    iconPosition === 'end' && btn('icon-end'),
    className,
  )

  const iconNode = loading ? (
    <span className={cx('om-btn-icon', 'om-btn-loading-icon')}>
      <LoadingIcon />
    </span>
  ) : icon != null ? (
    <span className="om-btn-icon">{icon}</span>
  ) : null

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    // antd: loading/disabled 시 클릭 차단(disabled 는 native attr 로도 차단됨).
    if (loading || disabled) return
    onClick?.(e)
  }

  return (
    <button
      type={htmlType}
      className={rootClass}
      style={style}
      disabled={disabled}
      onClick={handleClick}
      title={title}
      aria-label={ariaLabel}
      data-testid={dataTestid}
    >
      {iconNode}
      {children}
    </button>
  )
}
